import React, { useRef, useState } from 'react';
import { useVideoThumbnail } from '../../hooks/useVideoThumbnail';
import './VideoPreview.css';

interface VideoPreviewProps {
  /** Full video source URL. */
  src: string;
  /** Optional server-generated poster image (preferred over client-side frame extraction). */
  posterUrl?: string;
  /** Card class for sizing (cover-style thumbnail tile). */
  className?: string;
  /**
   * Autoplay a muted preview when the user hovers / focuses the card.
   * Mobile (no hover): tap-to-play handled by the underlying video controls when opened.
   * Always disabled if the user has prefers-reduced-motion: reduce.
   */
  hoverPreview?: boolean;
  /** Optional accessible alt text for the poster image. */
  alt?: string;
  /** Click handler — typically opens the full viewer. */
  onClick?: () => void;
}

const prefersReducedMotion = typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Reusable video THUMBNAIL/PREVIEW card.
 *
 * Contract (matches the audit doc):
 *  - Thumbnail tile may crop (`object-fit: cover`) — this component owns that.
 *  - Renders the server poster if provided; otherwise tries client-side
 *    middle-frame extraction via useVideoThumbnail; falls back to a
 *    play-icon placeholder on failure.
 *  - Optionally plays a muted, looping preview on hover/focus —
 *    NOT on mobile (no hover) and NOT for reduced-motion users.
 *  - Always shows a play badge so users know it's a video.
 *
 * For the OPEN viewer (story theater, post viewer, lightbox) use the
 * `.full-media-stage` pattern instead — that one uses `contain`.
 */
const VideoPreview: React.FC<VideoPreviewProps> = ({
  src,
  posterUrl,
  className = '',
  hoverPreview = false,
  alt = '',
  onClick,
}) => {
  const { thumbnail, status: extractStatus } = useVideoThumbnail(posterUrl ? undefined : src);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const poster = posterUrl || thumbnail || undefined;
  const canPreview = hoverPreview && !prefersReducedMotion && !previewError;

  const startPreview = () => {
    if (!canPreview) return;
    setPreviewing(true);
    queueMicrotask(() => {
      const v = videoRef.current;
      if (!v) return;
      v.muted = true; // browser autoplay policy
      v.play().catch(() => {
        setPreviewError(true);
        setPreviewing(false);
      });
    });
  };

  const stopPreview = () => {
    const v = videoRef.current;
    if (v) {
      try { v.pause(); v.currentTime = 0; } catch {}
    }
    setPreviewing(false);
  };

  return (
    <div
      className={`video-preview ${className}`}
      onClick={onClick}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      onFocus={startPreview}
      onBlur={stopPreview}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Loading skeleton */}
      {!poster && extractStatus === 'loading' && (
        <div className="video-preview-skeleton" aria-busy="true" aria-label="Generating preview" />
      )}

      {/* Failed to generate a thumbnail — intentional placeholder, not a broken icon */}
      {!poster && extractStatus === 'failed' && (
        <div className="video-preview-fallback" aria-label="Video preview unavailable">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <rect x="3" y="6" width="18" height="12" rx="2" />
            <path d="M10 10l5 2-5 2z" fill="currentColor" />
          </svg>
        </div>
      )}

      {/* Real frame as static <img> when not actively previewing */}
      {poster && !previewing && (
        <img
          src={poster}
          alt={alt}
          className="video-preview-img"
          loading="lazy"
          onError={() => setPreviewError(true)}
        />
      )}

      {/* Hover-triggered muted preview video */}
      {previewing && (
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload="metadata"
          className="video-preview-video"
          onError={() => { setPreviewError(true); setPreviewing(false); }}
        />
      )}

      {/* Always show a small play indicator so users know it's a video */}
      <div className={`video-preview-badge ${previewing ? 'is-playing' : ''}`} aria-hidden="true">
        {previewing ? (
          <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
        )}
      </div>
    </div>
  );
};

export default VideoPreview;
